/**
 * Changement du mot de passe maitre depuis le coffre deverrouille
 * (`docs/ARCHITECTURE.md` §1 : re-sceller la `vaultKey`, ne JAMAIS rechiffrer
 * le coffre). Aucune entree n'est touchee, aucune revision n'est incrementee :
 * seule l'enveloppe de compte change.
 *
 * Ordre volontaire, calque sur `account-recovery.ts` :
 *  1. re-scellement LOCAL (`changeMasterPasswordAsync`) -- un mot de passe
 *     actuel faux echoue ici, avant tout appel reseau ;
 *  2. si l'appareil est connecte a un serveur souverain : le serveur recoit
 *     preuve actuelle, preuve nouvelle, enveloppe re-scellee et parametres KDF
 *     (jamais un mot de passe, jamais la `vaultKey`). Il revoque TOUTES les
 *     sessions -- les autres appareils devront se reconnecter -- et emet la
 *     nouvelle session, reprise ici ;
 *  3. seulement ensuite l'enveloppe locale est persistee : un echec serveur
 *     laisse l'appareil exactement dans son etat d'avant (ancien mot de passe
 *     toujours valide partout).
 *
 * La cle de recuperation reste valable : elle scelle la `vaultKey` sous la cle
 * de recuperation, pas sous le mot de passe maitre.
 */
import { DecryptionFailedError, InvalidParameterError, changeMasterPasswordAsync } from '@klavox/crypto-core';
import type { VaultRepository } from './repository.js';
import { KlavoxApiError, changeServerMasterPassword } from './sync-client.js';
import type { BreachCheckOptions } from './breach-check.js';
import {
  ExposedMasterPasswordError,
  UnconfirmedWeakMasterPasswordError,
  WeakMasterPasswordError,
  assertNewMasterPasswordAccepted,
  assertNewMasterPasswordNotExposed,
} from './master-password-gate.js';
import type { AccountEnvelope, RemoteSession } from './types.js';

export {
  ExposedMasterPasswordError,
  MIN_MASTER_PASSWORD_LENGTH,
  UnconfirmedWeakMasterPasswordError,
  WeakMasterPasswordError,
} from './master-password-gate.js';

export interface ChangeAccountMasterPasswordInput {
  readonly repository: VaultRepository;
  readonly envelope: AccountEnvelope;
  readonly currentMasterPassword: string;
  readonly newMasterPassword: string;
  /** Consentement explicite a un nouveau mot de passe faible (`master-password-gate.ts`). */
  readonly acceptWeak?: boolean;
  /** Verification du nouveau mot de passe dans les fuites connues ; absente = non demandee, rien ne part. */
  readonly breachCheck?: BreachCheckOptions | null;
  /** Consentement explicite a un nouveau mot de passe present dans des fuites connues. */
  readonly acceptExposed?: boolean;
  /** Session serveur en cours ; `null` en mode local pur (re-scellement local seul). */
  readonly remoteSession: RemoteSession | null;
}

export interface ChangedAccountMasterPassword {
  readonly envelope: AccountEnvelope;
  /** Session renouvelee par le serveur (les precedentes sont revoquees) ; `null` en mode local. */
  readonly remoteSession: RemoteSession | null;
  /** Vrai si le serveur a ete mis a jour : les autres appareils sont deconnectes. */
  readonly serverUpdated: boolean;
}

export async function changeAccountMasterPassword(
  input: ChangeAccountMasterPasswordInput,
): Promise<ChangedAccountMasterPassword> {
  assertNewMasterPasswordAccepted(input.newMasterPassword, input.envelope.email, input.acceptWeak === true);
  await assertNewMasterPasswordNotExposed(input.newMasterPassword, input.breachCheck ?? null, input.acceptExposed === true);

  const changed = await changeMasterPasswordAsync({
    email: input.envelope.email,
    currentMasterPassword: input.currentMasterPassword,
    newMasterPassword: input.newMasterPassword,
    kdfParams: input.envelope.kdfParams,
    protectedSymmetricKey: input.envelope.protectedSymmetricKey,
  });

  let remoteSession = input.remoteSession;
  if (remoteSession !== null) {
    // La preuve du mot de passe ACTUEL est exigee par le serveur en plus de la
    // session : une session volee ne suffit pas a changer le mot de passe maitre.
    const session = await changeServerMasterPassword(remoteSession.baseUrl, remoteSession.sessionToken, {
      currentMasterPasswordHashBase64: changed.currentMasterPasswordHashBase64,
      newMasterPasswordHashBase64: changed.masterPasswordHashBase64,
      newProtectedSymmetricKey: changed.protectedSymmetricKey,
      newKdfParams: changed.kdfParams,
    });
    remoteSession = {
      ...remoteSession,
      sessionToken: session.sessionToken,
      refreshToken: session.refreshToken,
      expiresAt: session.expiresAt,
    };
  }

  const envelope: AccountEnvelope = {
    ...input.envelope,
    email: changed.email,
    kdfParams: changed.kdfParams,
    protectedSymmetricKey: changed.protectedSymmetricKey,
  };
  await input.repository.saveAccountEnvelope(envelope);
  return { envelope, remoteSession, serverUpdated: input.remoteSession !== null };
}

/** Message utilisateur sans detail technique : dit toujours ce qui a ete modifie, ou non. */
export function describeMasterPasswordChangeError(cause: unknown): string {
  if (cause instanceof WeakMasterPasswordError) return cause.message;
  if (cause instanceof ExposedMasterPasswordError) return `Nouveau mot de passe maitre present ${cause.occurrences.toLocaleString('fr-FR')} fois dans des fuites connues : rien n’a ete modifie. Choisissez-en un autre, ou cochez « Utiliser ce mot de passe expose quand meme ».`;
  if (cause instanceof UnconfirmedWeakMasterPasswordError) return `Nouveau mot de passe maitre faible (${Math.round(cause.bits)} bits) : rien n’a ete modifie. Choisissez-en un plus fort, ou cochez « Utiliser ce mot de passe faible quand meme ».`;
  if (cause instanceof DecryptionFailedError) return 'Mot de passe maitre actuel incorrect : rien n’a ete modifie.';
  if (cause instanceof InvalidParameterError) return 'Nouveau mot de passe maitre refuse : rien n’a ete modifie.';
  if (cause instanceof KlavoxApiError) {
    if (cause.code === 'authentication_failed') return 'Mot de passe maitre actuel refuse par le serveur : rien n’a ete modifie.';
    if (cause.code === 'session_expired') return 'Session serveur expiree : reconnectez la synchro, puis recommencez. Rien n’a ete modifie.';
    if (cause.code === 'network_error') return 'Serveur injoignable : rien n’a ete modifie, ni ici ni sur le serveur.';
  }
  return cause instanceof Error ? `Echec : ${cause.message}` : 'Echec du changement de mot de passe maitre';
}
